/**
 * Sidebar Component - Modern Clean Navigation
 * Responsive sidebar with permanent and temporary variants
 */

import React from 'react';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Box,
  Typography,
  Collapse,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate, useLocation } from 'react-router-dom';
import DashboardIcon from '@mui/icons-material/Dashboard';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import GridOnIcon from '@mui/icons-material/GridOn';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PendingActionsIcon from '@mui/icons-material/PendingActions';
import HistoryIcon from '@mui/icons-material/History';
import AssignmentIcon from '@mui/icons-material/Assignment';
import BarChartIcon from '@mui/icons-material/BarChart';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import SettingsIcon from '@mui/icons-material/Settings';
import IntegrationInstructionsIcon from '@mui/icons-material/IntegrationInstructions';
import InsightsIcon from '@mui/icons-material/Insights';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { useAuth } from '../../hooks/useAuth';
import { UserRole } from '../../types/User.types';
import { tokens } from '../../theme/tokens';

const DRAWER_WIDTH = 260;

interface SidebarProps {
  open: boolean;
  onClose: () => void;
  variant?: 'permanent' | 'persistent' | 'temporary';
}

interface MenuItem {
  title: string;
  path: string;
  icon: React.ReactElement;
  roles: UserRole[];
  children?: MenuItem[];
}

const StyledDrawer = styled(Drawer)({
  '& .MuiDrawer-paper': {
    width: DRAWER_WIDTH,
    boxSizing: 'border-box',
    background: '#FFFFFF',
    borderRight: '1px solid #E5E7EB',
    boxShadow: 'none',
    position: 'fixed',
    height: '100vh',
    overflowY: 'auto',
  },
});

const Sidebar: React.FC<SidebarProps> = ({ open, onClose, variant = 'temporary' }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [allocationOpen, setAllocationOpen] = React.useState(true);

  const menuItems: MenuItem[] = [
    {
      title: 'Dashboard',
      path: '/dashboard',
      icon: <InsightsIcon />,
      roles: [UserRole.SUPERVISOR, UserRole.MANAGER, UserRole.ADMIN],
    },
    {
      title: 'Allocation',
      path: '/allocation',
      icon: <GridOnIcon />,
      roles: [UserRole.SUPERVISOR, UserRole.MANAGER, UserRole.ADMIN],
      children: [
        {
          title: 'Upload Excel',
          path: '/upload',
          icon: <UploadFileIcon />,
          roles: [UserRole.SUPERVISOR, UserRole.ADMIN],
        },
        {
          title: 'Allocation Matrix',
          path: '/allocation/matrix',
          icon: <GridOnIcon />,
          roles: [UserRole.SUPERVISOR, UserRole.ADMIN],
        },
        {
          title: 'Approvals',
          path: '/allocation/approvals',
          icon: <PendingActionsIcon />,
          roles: [UserRole.MANAGER, UserRole.ADMIN],
        },
      ],
    },
    {
      title: 'Reports',
      path: '/reports',
      icon: <BarChartIcon />,
      roles: [UserRole.SUPERVISOR, UserRole.MANAGER, UserRole.ADMIN],
      title: 'Reports',
      path: '/reports',
      icon: <BarChartIcon />,
      roles: [UserRole.SUPERVISOR, UserRole.MANAGER, UserRole.ADMIN, UserRole.OPEX],
    },
    // Phase 2 — My Work is not yet implemented
    // {
    //   title: 'My Work',
    //   path: '/employee/my-work',
    //   icon: <AssignmentIcon />,
    //   roles: [UserRole.EMPLOYEE, UserRole.SUPERVISOR, UserRole.ADMIN],
    // },
    {
      title: 'History',
      path: '/history',
      icon: <HistoryIcon />,
      roles: [UserRole.SUPERVISOR, UserRole.MANAGER, UserRole.ADMIN],
    },
  ];

  return (
    <StyledDrawer
      variant={variant}
      anchor="left"
      open={open}
      onClose={onClose}
      ModalProps={{
        keepMounted: true,
      }}
    >
      {/* Header */}
      <Box sx={{ 
        height: '64px', 
        display: 'flex', 
        alignItems: 'center', 
        px: 3,
        borderBottom: '1px solid #E5E7EB',
      }}>
        <Typography variant="overline" sx={{ 
          fontWeight: 700, 
          color: '#9CA3AF',
          letterSpacing: '1px',
        }}>
          Navigation
        </Typography>
      </Box>

      {/* Navigation Menu */}
      <List component="nav" aria-label="Main navigation" sx={{ flexGrow: 1, pt: 2, px: 2 }}>
        {menuItems.map((item) => {
          // Hide items the current user's role is not permitted to see
          if (user?.role && !item.roles.includes(user.role)) return null;

          const isActive = location.pathname === item.path;
          const hasChildren = item.children && item.children.length > 0;

          return (
            <React.Fragment key={item.title}>
              <ListItem disablePadding sx={{ mb: 0.5 }}>
                <ListItemButton
                  aria-expanded={hasChildren ? allocationOpen : undefined}
                  aria-current={isActive ? 'page' : undefined}
                  onClick={() => {
                    if (hasChildren) {
                      setAllocationOpen(!allocationOpen);
                    } else {
                      navigate(item.path);
                      if (variant === 'temporary') {
                        onClose();
                      }
                    }
                  }}
                  sx={{
                    borderRadius: '8px',
                    py: 1.25,
                    px: 1.5,
                    minHeight: 44,
                    transition: 'all 0.15s ease',
                    '&:hover': {
                      backgroundColor: tokens.colors.neutral[50],
                    },
                    ...(isActive && {
                      backgroundColor: tokens.colors.neutral[100],
                      '& .MuiListItemIcon-root': {
                        color: tokens.colors.primary.main,
                      },
                      '& .MuiListItemText-primary': {
                        color: tokens.colors.primary.main,
                        fontWeight: 600,
                      },
                    }),
                  }}
                >
                  <ListItemIcon
                    sx={{
                      color: isActive ? tokens.colors.primary.main : tokens.colors.neutral[400],
                      minWidth: 36,
                      fontSize: 20,
                      transition: 'color 0.15s ease',
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.title}
                    primaryTypographyProps={{
                      fontWeight: isActive ? 600 : 500,
                      color: isActive ? tokens.colors.primary.main : tokens.colors.text.secondary,
                    }}
                  />
                  {hasChildren && (
                    allocationOpen ? 
                      <ExpandLess sx={{ color: '#9CA3AF', fontSize: 20 }} aria-hidden="true" /> : 
                      <ExpandMore sx={{ color: '#9CA3AF', fontSize: 20 }} aria-hidden="true" />
                  )}
                </ListItemButton>
              </ListItem>

              {/* Children (Submenu) */}
              {hasChildren && (
                <Collapse in={allocationOpen} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding sx={{ pl: 0.5 }}>
                    {item.children?.map((child) => {
                      const isChildActive = location.pathname === child.path;

                      return (
                        <ListItem key={child.title} disablePadding sx={{ mb: 0.5 }}>
                          <ListItemButton
                            onClick={() => {
                              navigate(child.path);
                              if (variant === 'temporary') {
                                onClose();
                              }
                            }}
                            sx={{
                              pl: 5.5,
                              py: 1,
                              borderRadius: tokens.borderRadius.md,
                              minHeight: 38,
                              transition: 'all 0.15s ease',
                              '&:hover': {
                                backgroundColor: tokens.colors.neutral[50],
                              },
                              ...(isChildActive && {
                                backgroundColor: tokens.colors.neutral[100],
                                '& .MuiListItemIcon-root': {
                                  color: tokens.colors.primary.main,
                                },
                                '& .MuiListItemText-primary': {
                                  color: tokens.colors.primary.main,
                                  fontWeight: 600,
                                },
                              }),
                            }}
                          >
                            <ListItemIcon
                              sx={{
                                color: isChildActive ? tokens.colors.primary.main : tokens.colors.neutral[400],
                                minWidth: 32,
                                fontSize: 18,
                              }}
                            >
                              {child.icon}
                            </ListItemIcon>
                            <ListItemText
                              primary={child.title}
                              primaryTypographyProps={{
                                fontWeight: isChildActive ? 600 : 400,
                                color: isChildActive ? tokens.colors.primary.main : tokens.colors.neutral[500],
                              }}
                            />
                          </ListItemButton>
                        </ListItem>
                      );
                    })}
                  </List>
                </Collapse>
              )}
            </React.Fragment>
          );
        })}
      </List>

      {/* Footer */}
      <Box sx={{ p: 3, borderTop: '1px solid #E5E7EB', backgroundColor: '#FAFBFC' }}>
        <Typography variant="overline" sx={{ display: 'block', mb: 0.5, color: '#9CA3AF', fontWeight: 600 }}>
          VERSION 1.0.0
        </Typography>
        <Typography variant="overline" sx={{ color: '#D1D5DB' }}>
          © 2026 Provana Inc.
        </Typography>
      </Box>
    </StyledDrawer>
  );
};

export default Sidebar;
